import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { PublicDiveSitePin } from '../services/buddyService';
import 'leaflet/dist/leaflet.css';

interface ProfileDiveMapProps {
  sites: PublicDiveSitePin[];
  height?: number;
}

const FitBounds: React.FC<{ sites: PublicDiveSitePin[] }> = ({ sites }) => {
  const map = useMap();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!map || !map.getContainer()) {
      return;
    }

    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (!isInitialized || !map || !map.getContainer() || sites.length === 0) {
      return;
    }

    try {
      if (sites.length === 1) {
        const site = sites[0];
        map.setView([site.latitude, site.longitude], 7, { animate: false });
        return;
      }

      const bounds = L.latLngBounds(sites.map((site) => [site.latitude, site.longitude]));
      map.fitBounds(bounds, { padding: [24, 24], animate: false });
    } catch (error) {
      console.error('Error fitting profile dive map bounds:', error);
    }
  }, [sites, map, isInitialized]);

  return null;
};

// Sized (not colored) by dive count — how often this diver returns to a site is the
// more interesting signal on a profile than depth, which varies dive-to-dive anyway.
const pinSizeForDiveCount = (diveCount: number): number => {
  if (diveCount >= 10) return 40;
  if (diveCount >= 5) return 34;
  return 28;
};

const createSiteIcon = (site: PublicDiveSitePin): L.DivIcon => {
  const height = pinSizeForDiveCount(site.diveCount);
  const width = Math.round(height * (28 / 44));

  const svgIcon = `
    <svg width="${width}" height="${height}" viewBox="0 0 28 44" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0C6.3 0 0 6.3 0 14C0 24.4 14 44 14 44S28 24.4 28 14C28 6.3 21.7 0 14 0Z" fill="#00838F" stroke="#ffffff" stroke-width="2"/>
      <circle cx="14" cy="14" r="5" fill="white"/>
    </svg>
  `;

  return new L.DivIcon({
    html: svgIcon,
    className: 'custom-profile-dive-map-marker',
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height + 4],
  } as L.DivIconOptions);
};

const RenderedMap: React.FC<{ sites: PublicDiveSitePin[]; height: number; mapKey: number }> = ({ sites, height, mapKey }) => {
  return (
    <MapContainer
      key={mapKey}
      style={{ height: `${height}px`, width: '100%' }}
      zoom={2}
      center={[sites[0].latitude, sites[0].longitude]}
      zoomControl={true}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <FitBounds sites={sites} />

      {sites.map((site) => (
        <Marker
          key={`${site.latitude},${site.longitude}`}
          position={[site.latitude, site.longitude]}
          icon={createSiteIcon(site)}
        >
          <Popup>
            <Box sx={{ minWidth: { xs: 'min(200px, 70vw)', sm: 200 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {site.location || 'Dive site'}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" color="primary" label={`Dove ${site.diveCount}x`} />
                <Chip size="small" variant="outlined" label={`Max ${site.maxDepth.toFixed(0)}m`} />
              </Stack>
            </Box>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

const ProfileDiveMap: React.FC<ProfileDiveMapProps> = ({ sites, height = 320 }) => {
  const [mapKey, setMapKey] = useState(0);

  useEffect(() => {
    setMapKey((currentValue) => currentValue + 1);
  }, [sites.length]);

  if (sites.length === 0) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
      <RenderedMap sites={sites} height={height} mapKey={mapKey} />
    </Paper>
  );
};

export default ProfileDiveMap;
